import type { SerializableRecord } from '../model';
import { compactPayloadEntrySys } from './compactPayloadEntrySys';
import { isSerializableRecord } from './isSerializableRecord';
import { getContentTypeId } from './getContentTypeId';

/**
 * Content types that can be resolved as internal/link page targets in Contentful.
 * Keep in sync with page content types referenced from link-like fields in Contentful.
 */
export const RESOLVABLE_PAGE_CONTENT_TYPE_IDS = [
  'contentPage',
  'download'
] as const;

export type ResolvablePageContentTypeId =
  (typeof RESOLVABLE_PAGE_CONTENT_TYPE_IDS)[number];

export type PageReferencePurpose = 'link' | 'teaser';

const LINK_REFERENCE_FIELDS = ['slug'] as const;

const TEASER_REFERENCE_FIELDS = [
  'slug',
  'teaserTitle',
  'teaserText',
  'teaserImage'
] as const;

export function isResolvablePageContentType(
  contentTypeId: unknown
): contentTypeId is ResolvablePageContentTypeId {
  return (
    typeof contentTypeId === 'string' &&
    (RESOLVABLE_PAGE_CONTENT_TYPE_IDS as readonly string[]).includes(
      contentTypeId
    )
  );
}

export function resolvePageReferencePurpose(
  parentFieldKey?: string
): PageReferencePurpose {
  return parentFieldKey === 'teasers' ? 'teaser' : 'link';
}

function pickReferenceFields(
  fields: SerializableRecord,
  keys: readonly string[]
): SerializableRecord {
  const compactedFields: SerializableRecord = {};

  for (const key of keys) {
    const fieldValue = fields[key];
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      compactedFields[key] = fieldValue;
    }
  }

  return compactedFields;
}

/**
 * Strips a resolved page/download entry down to the fields required in the
 * client payload when it is referenced from another entry (not the root page).
 */
export function compactResolvablePageReference(
  value: SerializableRecord,
  purpose: PageReferencePurpose
): SerializableRecord | undefined {
  const contentTypeId = getContentTypeId(value);
  if (!isResolvablePageContentType(contentTypeId)) {
    return undefined;
  }

  const sys = compactPayloadEntrySys(value.sys);
  const fields = isSerializableRecord(value.fields) ? value.fields : undefined;

  if (!sys || !fields) {
    return undefined;
  }

  const fieldKeys =
    purpose === 'teaser' ? TEASER_REFERENCE_FIELDS : LINK_REFERENCE_FIELDS;
  const compactedFields = pickReferenceFields(fields, fieldKeys);

  if (Object.keys(compactedFields).length === 0) {
    return undefined;
  }

  return {
    sys,
    fields: compactedFields
  };
}
